import React, { useState, useRef, useEffect } from 'react'
import { Play, Pause, Square, SkipBack, SkipForward, Volume2, VolumeX, Repeat, Shuffle } from 'lucide-react'
import { useApp } from '../context/AppContext.jsx'
import toast from 'react-hot-toast'

function AudioPlayer() {
  const {
    audioFiles,
    currentAudio,
    isPlaying,
    playAudio,
    pauseAudio,
    stopAudio,
    setIsPlaying
  } = useApp()

  const audioRef = useRef(null)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [volume, setVolume] = useState(1)
  const [isMuted, setIsMuted] = useState(false)
  const [isRepeat, setIsRepeat] = useState(false)
  const [isShuffle, setIsShuffle] = useState(false)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [playbackRate, setPlaybackRate] = useState(1)

  // Update current index when currentAudio changes
  useEffect(() => {
    if (currentAudio) {
      const index = audioFiles.findIndex(audio => audio.id === currentAudio.id)
      setCurrentIndex(index >= 0 ? index : 0)
    }
  }, [currentAudio, audioFiles])

  // Handle audio events
  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime)
    }

    const handleDurationChange = () => {
      setDuration(audio.duration)
    }

    const handleEnded = () => {
      if (isRepeat) {
        audio.currentTime = 0
        audio.play()
      } else {
        handleNext()
      }
    }

    const handlePlay = () => {
      setIsPlaying(true)
    }

    const handlePause = () => {
      setIsPlaying(false)
    }

    audio.addEventListener('timeupdate', handleTimeUpdate)
    audio.addEventListener('durationchange', handleDurationChange)
    audio.addEventListener('ended', handleEnded)
    audio.addEventListener('play', handlePlay)
    audio.addEventListener('pause', handlePause)

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate)
      audio.removeEventListener('durationchange', handleDurationChange)
      audio.removeEventListener('ended', handleEnded)
      audio.removeEventListener('play', handlePlay)
      audio.removeEventListener('pause', handlePause)
    }
  }, [isRepeat])

  // Update audio source when currentAudio changes
  useEffect(() => {
    const audio = audioRef.current
    if (!audio || !currentAudio) return

    audio.src = currentAudio.audioUrl
    audio.volume = volume
    audio.playbackRate = playbackRate

    if (isPlaying) {
      audio.play().catch(error => {
        console.error('Error playing audio:', error)
        toast.error('Failed to play audio')
        setIsPlaying(false)
      })
    }
  }, [currentAudio])

  // Handle play/pause
  useEffect(() => {
    const audio = audioRef.current
    if (!audio || !currentAudio) return

    if (isPlaying) {
      audio.play().catch(error => {
        console.error('Error playing audio:', error)
        toast.error('Failed to play audio')
        setIsPlaying(false)
      })
    } else {
      audio.pause()
    }
  }, [isPlaying])

  const handlePlayPause = () => {
    if (!currentAudio) {
      if (audioFiles.length > 0) {
        playAudio(audioFiles[0])
      } else {
        toast.error('No audio files available')
      }
      return
    }

    if (isPlaying) {
      pauseAudio()
    } else {
      setIsPlaying(true)
    }
  }

  const handleStop = () => {
    stopAudio()
    setCurrentTime(0)
    if (audioRef.current) {
      audioRef.current.currentTime = 0
    }
  }

  const handlePrevious = () => {
    if (audioFiles.length === 0) return

    let newIndex
    if (isShuffle) {
      newIndex = Math.floor(Math.random() * audioFiles.length)
    } else {
      newIndex = currentIndex > 0 ? currentIndex - 1 : audioFiles.length - 1
    }

    playAudio(audioFiles[newIndex])
  }

  const handleNext = () => {
    if (audioFiles.length === 0) return

    let newIndex
    if (isShuffle) {
      newIndex = Math.floor(Math.random() * audioFiles.length)
    } else {
      newIndex = currentIndex < audioFiles.length - 1 ? currentIndex + 1 : 0
    }

    playAudio(audioFiles[newIndex])
  }

  const handleSeek = (e) => {
    const audio = audioRef.current
    if (!audio || !duration) return

    const rect = e.currentTarget.getBoundingClientRect()
    const percent = (e.clientX - rect.left) / rect.width
    const newTime = percent * duration

    audio.currentTime = newTime
    setCurrentTime(newTime)
  }

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value)
    setVolume(newVolume)
    setIsMuted(newVolume === 0)

    if (audioRef.current) {
      audioRef.current.volume = newVolume
    }
  }

  const toggleMute = () => {
    const newMuted = !isMuted
    setIsMuted(newMuted)

    if (audioRef.current) {
      audioRef.current.volume = newMuted ? 0 : volume
    }
  }

  const handlePlaybackRateChange = (rate) => {
    setPlaybackRate(rate)
    if (audioRef.current) {
      audioRef.current.playbackRate = rate
    }
  }

  const formatTime = (time) => {
    if (!time || !isFinite(time)) return '0:00'

    const minutes = Math.floor(time / 60)
    const seconds = Math.floor(time % 60)
    return `${minutes}:${seconds.toString().padStart(2, '0')}`
  }

  const progressPercent = duration ? (currentTime / duration) * 100 : 0

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Audio Player
        </h1>
        <p className="text-gray-600">
          Play, control, and manage your generated speech audio files.
        </p>
      </div>

      {/* Hidden Audio Element */}
      <audio ref={audioRef} />

      {/* Main Player */}
      <div className="card mb-8">
        {currentAudio ? (
          <div className="space-y-6">
            {/* Now Playing Info */}
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                {currentAudio.title}
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto line-clamp-2">
                {currentAudio.text}
              </p>
              <div className="flex items-center justify-center gap-4 mt-4 text-sm text-gray-500">
                <span>Voice: {currentAudio.voiceSettings?.gender} • {currentAudio.voiceSettings?.accent}</span>
                <span>•</span>
                <span>Created: {new Date(currentAudio.createdAt).toLocaleDateString()}</span>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="space-y-2">
              <div
                className="w-full h-2 bg-gray-200 rounded-full cursor-pointer overflow-hidden"
                onClick={handleSeek}
              >
                <div
                  className="h-full bg-primary-500 transition-all duration-100"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
              <div className="flex items-center justify-between text-sm text-gray-500">
                <span>{formatTime(currentTime)}</span>
                <span>{formatTime(duration)}</span>
              </div>
            </div>

            {/* Main Controls */}
            <div className="flex items-center justify-center gap-4">
              <button
                onClick={handlePrevious}
                disabled={audioFiles.length <= 1}
                className="btn-secondary p-3 disabled:opacity-50"
              >
                <SkipBack className="w-5 h-5" />
              </button>

              <button
                onClick={handlePlayPause}
                className="btn-primary p-4 rounded-full"
              >
                {isPlaying ? (
                  <Pause className="w-6 h-6" />
                ) : (
                  <Play className="w-6 h-6" />
                )}
              </button>

              <button
                onClick={handleStop}
                className="btn-secondary p-3"
              >
                <Square className="w-5 h-5" />
              </button>

              <button
                onClick={handleNext}
                disabled={audioFiles.length <= 1}
                className="btn-secondary p-3 disabled:opacity-50"
              >
                <SkipForward className="w-5 h-5" />
              </button>
            </div>

            {/* Secondary Controls */}
            <div className="flex items-center justify-between">
              {/* Left Controls */}
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setIsRepeat(!isRepeat)}
                  className={`btn-secondary p-2 ${isRepeat ? 'bg-primary-100 text-primary-700' : ''}`}
                >
                  <Repeat className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setIsShuffle(!isShuffle)}
                  className={`btn-secondary p-2 ${isShuffle ? 'bg-primary-100 text-primary-700' : ''}`}
                >
                  <Shuffle className="w-4 h-4" />
                </button>
              </div>

              {/* Center - Playback Speed */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Speed:</span>
                {[0.5, 0.75, 1, 1.25, 1.5, 2].map((rate) => (
                  <button
                    key={rate}
                    onClick={() => handlePlaybackRateChange(rate)}
                    className={`px-2 py-1 text-xs rounded ${playbackRate === rate
                        ? 'bg-primary-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                  >
                    {rate}x
                  </button>
                ))}
              </div>

              {/* Right Controls - Volume */}
              <div className="flex items-center gap-2">
                <button
                  onClick={toggleMute}
                  className="btn-secondary p-2"
                >
                  {isMuted ? (
                    <VolumeX className="w-4 h-4" />
                  ) : (
                    <Volume2 className="w-4 h-4" />
                  )}
                </button>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={isMuted ? 0 : volume}
                  onChange={handleVolumeChange}
                  className="w-20 h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-16">
            <Play className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              No Audio Selected
            </h3>
            <p className="text-gray-600 mb-6">
              {audioFiles.length === 0
                ? 'You haven\'t created any audio files yet. Go to Text Input to create your first audio.'
                : 'Select an audio file from the playlist below to start playing.'
              }
            </p>
            {audioFiles.length > 0 && (
              <button
                onClick={() => playAudio(audioFiles[0])}
                className="btn-primary"
              >
                Play First Audio
              </button>
            )}
          </div>
        )}
      </div>

      {/* Playlist */}
      {audioFiles.length > 0 && (
        <div className="card">
          <div className="card-header">
            <h3 className="text-xl font-semibold text-gray-900">
              Playlist ({audioFiles.length} files)
            </h3>
          </div>

          <div className="space-y-2">
            {audioFiles.map((audio, index) => (
              <div
                key={audio.id}
                className={`flex items-center gap-4 p-3 rounded-lg cursor-pointer transition-colors ${currentAudio?.id === audio.id
                    ? 'bg-primary-50 border border-primary-200'
                    : 'hover:bg-gray-50'
                  }`}
                onClick={() => playAudio(audio)}
              >
                <div className="flex-shrink-0">
                  {currentAudio?.id === audio.id && isPlaying ? (
                    <div className="flex items-center gap-1">
                      {[...Array(4)].map((_, i) => (
                        <div
                          key={i}
                          className="w-1 bg-primary-500 rounded wave-animation"
                          style={{ height: Math.random() * 20 + 10 }}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center text-sm font-medium text-gray-600">
                      {index + 1}
                    </div>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-gray-900 truncate">
                    {audio.title}
                  </h4>
                  <p className="text-sm text-gray-600 truncate">
                    {audio.text.substring(0, 80)}...
                  </p>
                </div>

                <div className="text-sm text-gray-500">
                  {formatTime(audio.duration || 0)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Audio Insights Section */}
      <div className="mt-20 border-t border-gray-200 pt-16">
        <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Understanding Audio Playback & Control</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                <div className="w-8 h-8 bg-primary-100 text-primary-600 rounded-lg flex items-center justify-center">
                  <Play className="w-4 h-4" />
                </div>
                The Logic of Playback Control
              </h3>
              <p className="text-gray-600 leading-relaxed">
                The Voice Converter Pro audio player is designed with advanced logic to ensure a smooth listening experience.
                When you play a file, the system dynamically buffers the audio data generated from our neural voice models.
                Features like <strong>Seek</strong> and <strong>Playback Speed</strong> allow you to navigate through text-heavy
                conversions efficiently, making it easy to review specific sections of your content.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                <div className="w-8 h-8 bg-accent-100 text-accent-600 rounded-lg flex items-center justify-center">
                  <Volume2 className="w-4 h-4" />
                </div>
                Optimizing Audio Quality
              </h3>
              <p className="text-gray-600 leading-relaxed">
                To get the best clarity from your generated speech, we recommend keeping the volume at a balanced level.
                Our player uses high-fidelity audio drivers that maintain the integrity of the voice's natural harmonics even
                at different playback speeds. Whether you're slowing it down to 0.5x or speeding it up to 2x,
                the pitch remains compensated to prevent the "chipmunk" or "deep voice" effect common in older technologies.
              </p>
            </div>
          </div>

          <div className="bg-gray-50 p-8 rounded-3xl border border-gray-100">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h3>
            <div className="space-y-4">
              <details className="group bg-white p-4 rounded-xl shadow-sm border border-gray-100 cursor-pointer">
                <summary className="font-semibold text-gray-900 list-none flex justify-between items-center">
                  How do I use the shuffle and repeat features?
                  <span className="group-open:rotate-180 transition-transform">↓</span>
                </summary>
                <p className="mt-3 text-sm text-gray-600">
                  The repeat feature allows you to loop a single audio file indefinitely—useful for memorization or ambient content.
                  Shuffle randomly picks the next file in your playlist, which is great for listening back to
                  a variety of your recent conversions.
                </p>
              </details>
              <details className="group bg-white p-4 rounded-xl shadow-sm border border-gray-100 cursor-pointer">
                <summary className="font-semibold text-gray-900 list-none flex justify-between items-center">
                  Why does the audio sometimes take a moment to load?
                  <span className="group-open:rotate-180 transition-transform">↓</span>
                </summary>
                <p className="mt-3 text-sm text-gray-600">
                  Each audio file is generated in real-time using complex neural networks.
                  While most conversions take just a split second, longer texts may require a bit more
                  computational time to ensure the highest vocal quality and natural sounding prosody.
                </p>
              </details>
              <details className="group bg-white p-4 rounded-xl shadow-sm border border-gray-100 cursor-pointer">
                <summary className="font-semibold text-gray-900 list-none flex justify-between items-center">
                  Can I control playback with my keyboard?
                  <span className="group-open:rotate-180 transition-transform">↓</span>
                </summary>
                <p className="mt-3 text-sm text-gray-600">
                  Yes! We are working on keyboard shortcut integrations. Currently, you can use the Tab key
                  to navigate through the interface and the Spacebar or Enter key to interact with
                  the focused controls.
                </p>
              </details>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AudioPlayer
